// Apolo IDE — useDualStream (Sprint v11 E8 P6 second pass).
//
// Drives the /api/code-ide/dual-model SSE pipeline. Returns:
//   { send, streaming, plan, execution, phase, error, abort }
// where:
//   - plan: accumulated text from Architect phase
//   - execution: accumulated text from Implementer phase
//   - phase: 'idle' | 'architect' | 'implementer' | 'done' | 'error'

import { useCallback, useEffect, useRef, useState } from 'react';
import { streamSSE } from '../lib/sseClient.js';

export function useDualStream() {
  const [streaming, setStreaming] = useState(false);
  const [plan, setPlan] = useState('');
  const [execution, setExecution] = useState('');
  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const abort = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStreaming(false);
    setPhase('idle');
  }, []);

  const send = useCallback(async ({
    userMessage,
    context,
    architectModelId = 'anthropic/claude-sonnet-4-6',
    implementerModelId = 'deepseek/deepseek-flash',
    sessionId,
  }) => {
    if (!userMessage?.trim()) return { ok: false, error: 'empty input' };
    setError(null);
    setPlan('');
    setExecution('');
    setPhase('architect');
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    const result = await streamSSE('/api/code-ide/dual-model', {
      body: {
        session_id: sessionId,
        user_message: userMessage,
        context,
        architect_model_id: architectModelId,
        implementer_model_id: implementerModelId,
      },
      signal: controller.signal,
      onChunk: (chunk) => {
        if (!chunk) return;
        if (chunk.type === 'phase_start') {
          setPhase(chunk.phase);
          return;
        }
        if (chunk.type === 'text' && typeof chunk.delta === 'string') {
          if (chunk.phase === 'architect') setPlan((p) => p + chunk.delta);
          else if (chunk.phase === 'implementer') setExecution((p) => p + chunk.delta);
        }
        if (chunk.type === 'error') {
          setError(chunk.message || 'dual stream error');
          setPhase('error');
        }
        if (chunk.type === 'done') {
          setPhase('done');
        }
      },
    });
    abortRef.current = null;
    setStreaming(false);
    if (!result.ok) {
      setError(result.error || 'falha de stream');
      setPhase('error');
      return { ok: false, error: result.error };
    }
    return { ok: true, plan, execution };
  }, [plan, execution]);

  return { send, streaming, plan, execution, phase, error, abort };
}
